import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import GetCookie from "../../utils/GetCookie";
import ReadMoreTruncateText from "../../utils/ReadMoreTruncateText";

const useOutsideClick = (ref, callback, disabled = false) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (disabled) return;
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, disabled]);
};

const GenbiTemplateLibraryList = ({
  isOpen,
  onClose,
  onImport,
  onError,
  onProcessingStart,
  onProcessingEnd,
}) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [expandedTagsTemplateId, setExpandedTagsTemplateId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  const listRef = useRef(null);
  useOutsideClick(listRef, handleClose);

  const API_BASE_URL = "/pceapi/genbi";
  const headers = { Authorization: GetCookie("access_token") };

  // Fetch library templates
  useEffect(() => {
    if (!isOpen || templates.length > 0) return;
    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/list-template-libraries`, { headers });
        if (response.data.status === "success") {
          setTemplates(response.data.data);
        } else {
          onError(response.data.message || "Failed to fetch templates.");
        }
      } catch (error) {
        onError("An error occurred while fetching templates.");
        console.error("Error fetching templates:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
  }, [isOpen]);

  // ── Clicking "Import" on a library card ──────────────────────────────────
  // Imports the library template structure directly into the form. The library
  // template id is passed up alongside the structure so the caller can offer an
  // optional "Import from Document" follow-up mapped against this same template.
  const handleImportClick = async (templateId) => {
    setImportingId(templateId);
    if (onProcessingStart) onProcessingStart("Importing template...");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/import-template-library`,
        { library_template_id: templateId },
        { headers }
      );
      if (response.data.status === "success") {
        onImport({ ...response.data.data, library_template_id: templateId });
        handleClose();
      } else {
        onError(response.data.message || "Failed to import template.");
      }
    } catch (error) {
      onError("An error occurred while importing the template.");
      console.error("Error importing template:", error);
    } finally {
      setImportingId(null);
      if (onProcessingEnd) onProcessingEnd();
    }
  };

  const toggleTagsExpansion = (templateId) => {
    setExpandedTagsTemplateId((prevId) => (prevId === templateId ? null : templateId));
  };

  if (!isOpen) return null;

  // ── Library list ──────────────────────────────────────────────────────────
  const renderLibraryTab = () => {
    const filteredTemplates = templates.filter((t) =>
      t.library_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
    <div className="ev-padding-small">
      {!isLoading && templates.length > 0 && (
        <div className="mb-2 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search Templates"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="ev-btn-lightblue-fill-round search-bar-button">
            <i className="bi bi-search"></i>
          </button>
        </div>
      )}
      <div className="scrollable-element vh-15">
      {isLoading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div>
          {filteredTemplates.map((template) => {
            const showAllTags = expandedTagsTemplateId === template.id;
            const visibleTags = showAllTags
              ? template.tags
              : template.tags?.slice(0, 2) || [];
            return (
              <div
                key={template.id}
                className="ev-card-7 d-flex justify-content-between align-items-center mb-2"
              >
                <div className="flex-grow-1 pe-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <p className="mb-1 ev-reg-p-blue">{template.library_name}</p>
                    <button
                      className="ev-btn-blue-fill-round ev-bold-reg px-4"
                      onClick={() => handleImportClick(template.id)}
                      disabled={importingId === template.id}
                    >
                      {importingId === template.id ? (
                        <div className="px-2">
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        </div>
                      ) : (
                        "Import"
                      )}
                    </button>
                  </div>
                  <ReadMoreTruncateText text={template.description} limit={50} />
                  {template.tags && template.tags.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                      {visibleTags.map((tag, idx) => (
                        <span key={idx} className="ev-message-card6 border-0 mt-1">
                          {tag}
                        </span>
                      ))}
                      {template.tags.length > 2 && !showAllTags && (
                        <span
                          className="more-circle more-circle--small ev-btn-blue-outline-round ev-bold-reg"
                          onClick={() => toggleTagsExpansion(template.id)}
                          role="button"
                        >
                          +{template.tags.length - 2}
                        </span>
                      )}
                      {showAllTags && (
                        <span
                          className="more-circle more-circle--small ev-btn-blue-outline-round ev-bold-reg"
                          onClick={() => toggleTagsExpansion(template.id)}
                          role="button"
                        >
                          <i className="bi bi-dash"></i>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ev-alert-card-warning text-center py-3">
          <p className="mb-0">
            <i className="bi bi-info-circle me-1"></i>
            {templates.length > 0
              ? "No templates match your search."
              : "No templates are available in the library."}
          </p>
        </div>
      )}
      </div>
    </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div
      ref={listRef}
      className="ev-message-card2 d-block ev-card-3 overflow-hidden position-absolute z-1000 mt-1 p-0"
      style={{ width: "35rem", maxWidth: "90vw" }}
    >
      {renderLibraryTab()}
    </div>
  );
};

export default GenbiTemplateLibraryList;